import { useEffect, useState } from "react";
import { Bell, Heart, MessageCircle, CheckCircle2, XCircle, Trash2, CheckCheck, UserPlus, AtSign, Send } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { useAuth, authClient } from "@/lib/auth";
import { useRealtime } from "@/lib/realtime";
import { sfx } from "@/lib/sounds";
import { toast } from "sonner";

const TYPE_ICON = { like: Heart, comment: MessageCircle, approved: CheckCircle2, rejected: XCircle, follow: UserPlus, mention: AtSign, message: Send };
const TYPE_COLOR = { like: "#FF3B7A", comment: "#3ABEFF", approved: "#22c55e", rejected: "#FF3B3B", follow: "#FFBF00", mention: "#FF8C00", message: "#3ABEFF" };
const TYPE_LABEL = { like: "liked", comment: "commented on", approved: "approved", rejected: "rejected", follow: "follow", mention: "mentioned", message: "message" };

export default function NotificationsBell() {
  const { user } = useAuth();
  const rt = useRealtime();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const loadCount = async () => {
    try {
      const { data } = await authClient.get("/notifications/unread-count");
      setUnread(data.count || 0);
    } catch (err) { console.warn("notif count failed", err?.message); }
  };

  const loadList = async () => {
    try {
      const { data } = await authClient.get("/notifications");
      setItems(data);
    } catch (err) { console.warn("notif list failed", err?.message); }
  };

  useEffect(() => {
    if (!user) { setItems([]); setUnread(0); return; }
    loadCount();
    loadList();
    // Fallback poll (only fires when WS is disconnected)
    const id = setInterval(() => { if (!rt.connected) loadCount(); }, 25000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Realtime — push notifications instantly to the list + badge
  useEffect(() => {
    if (!user) return () => {};
    const un = rt.subscribe("notification", (msg) => {
      const n = msg.notification;
      if (!n) return;
      setItems((prev) => prev.some(x => x.id === n.id) ? prev : [n, ...prev].slice(0, 40));
      setUnread((u) => u + 1);
    });
    return () => un();
  }, [user, rt]);

  useEffect(() => {
    if (open && user) loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const markRead = async (id) => {
    setItems(items.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread(Math.max(0, unread - 1));
    try { await authClient.post(`/notifications/read/${id}`); }
    catch (err) { console.warn("mark read failed", err?.message); }
  };

  const markAllRead = async () => {
    setItems(items.map((n) => ({ ...n, read: true })));
    setUnread(0);
    sfx.click();
    try { await authClient.post("/notifications/read-all"); }
    catch (err) { console.warn("mark all read failed", err?.message); }
  };

  const remove = async (id) => {
    setItems(items.filter((n) => n.id !== id));
    try { await authClient.delete(`/notifications/${id}`); }
    catch (err) { console.warn("remove notif failed", err?.message); }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear all notifications?")) return;
    setItems([]); setUnread(0);
    sfx.no();
    try { await authClient.delete("/notifications"); toast.success("Cleared"); }
    catch (err) { console.warn("clear notifs failed", err?.message); toast.error("Failed to clear"); }
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="nav-notifications"
          onClick={() => sfx.click()}
          className="relative p-2 rounded-full hover:bg-white/5 border border-transparent hover:border-white/10 text-white/80"
          aria-label="Notifications"
        >
          <Bell size={18} />
          {unread > 0 && (
            <span
              data-testid="notif-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FFBF00] text-black text-[10px] font-bold grid place-items-center leading-none"
              style={{ boxShadow: "0 0 12px rgba(255,191,0,0.6)" }}
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 max-w-[92vw] p-0 bg-black/95 border border-white/10 text-white backdrop-blur-xl" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFBF00]">Notifications</div>
          <div className="flex items-center gap-1">
            {unread > 0 && (
              <button
                data-testid="notif-mark-all-read"
                onClick={markAllRead}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/70 hover:text-[#FFBF00] px-2 py-1 rounded-md hover:bg-white/5"
              >
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
            {items.length > 0 && (
              <button
                data-testid="notif-clear-all"
                onClick={clearAll}
                className="text-[10px] uppercase tracking-widest text-white/40 hover:text-red-400 px-2 py-1 rounded-md hover:bg-white/5"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto divide-y divide-white/5">
          {items.length === 0 && (
            <div className="text-center text-white/40 text-sm px-4 py-10">
              You're all caught up.
            </div>
          )}
          {items.map((n) => {
            const Icon = TYPE_ICON[n.type] || Bell;
            const color = TYPE_COLOR[n.type] || "#FFBF00";
            const label = TYPE_LABEL[n.type] || n.type;
            let when = "";
            try { when = formatDistanceToNow(new Date(n.created_at), { addSuffix: true }); } catch (err) { console.debug("date fmt failed", err?.message); }
            return (
              <div
                key={n.id}
                data-testid={`notif-item-${n.id}`}
                className={`group flex items-start gap-3 px-4 py-3 hover:bg-white/5 ${n.read ? "opacity-70" : ""}`}
              >
                {n.actor_avatar ? (
                  <div className="relative">
                    <img src={n.actor_avatar} alt="" className="w-9 h-9 rounded-md object-cover" />
                    <div
                      className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full grid place-items-center border-2 border-black"
                      style={{ background: color }}
                    >
                      <Icon size={10} className="text-black" fill="currentColor" />
                    </div>
                  </div>
                ) : (
                  <div className="w-9 h-9 rounded-md grid place-items-center" style={{ background: `${color}33`, color }}>
                    <Icon size={14} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <Link
                    to={
                      n.subject_type === "build" ? "/builds"
                      : n.subject_type === "post" ? `/community#${n.subject_id || ""}`
                      : n.subject_type === "user" ? `/users/${n.subject_id || ""}`
                      : n.subject_type === "follow_request" ? "/follow-requests"
                      : n.subject_type === "conversation" ? `/messages?c=${n.subject_id || ""}`
                      : "/"
                    }
                    onClick={() => { markRead(n.id); setOpen(false); }}
                    className="text-sm"
                  >
                    <span className="font-semibold text-white">{n.actor_name || "Someone"}</span>{" "}
                    <span className="text-white/60">{label}</span>{" "}
                    <span className="font-semibold text-[#FFBF00]">{n.subject_title}</span>
                  </Link>
                  {n.body && n.type === "comment" && (
                    <div className="text-xs text-white/60 mt-1 truncate">"{n.body}"</div>
                  )}
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">{when}</div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!n.read && (
                    <button
                      data-testid={`notif-read-${n.id}`}
                      onClick={() => markRead(n.id)}
                      className="p-1 rounded hover:bg-white/10 text-white/50 hover:text-[#FFBF00]"
                      aria-label="Mark read"
                    >
                      <CheckCircle2 size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => remove(n.id)}
                    className="p-1 rounded hover:bg-red-500/10 text-white/40 hover:text-red-400"
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-[#FFBF00] shrink-0 mt-2" />}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
